// ─── Group Livestream shared base ─────────────────────────────────
// Logic chung cho GroupTaixiuLivestreamModal + GroupXocdiaLivestreamModal + GroupTulinhLivestreamModal.
//   - Subscribe shared session data qua useSharedSession (đã parse sẵn trên server)
//   - Không có SSE logs console (group chỉ xem session, không xem raw logs)
//   - Header giữ trạng thái hoạt động của các thành viên (member dots)
//
// Mỗi modal game-specific sẽ:
//   - render UI riêng (dice vs đĩa đỏ/trắng)
//   - dùng session.lastResult theo kiểu result của game đó

import React from 'react';
import { IAccountDataResponse } from '@tool-no-hu/shared/types';
import { useSharedSession, type SharedSessionStatus } from '../../hooks/useSharedSession';
import {
  formatJackpot,
  formatCoin,
  type SessionState,
} from '../../hooks/useLivestreamSession';

// ─── Types ──────────────────────────────────────────────────────────

export interface GroupLivestreamModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  groupId: string;
  members: IAccountDataResponse[];
  /** Game của group, đã resolve ở router modal — dùng làm khoá SSE dùng chung. */
  gameType?: string;
}

// ─── Shared helpers ────────────────────────────────────────────────

/** Parse typegame từ account.config (vd: "[TX]..." → "TX") */
export { parseGameType as parseTypegame } from '../../utils/game';

export function gameName(typegame: string): string {
  if (typegame === 'TX') return 'Tài Xỉu';
  if (typegame === 'XD') return 'Xóc Đĩa';
  if (typegame === 'TL') return 'Tứ Linh';
  return typegame;
}

// ─── Shared UI helpers (color classes) ─────────────────────────────

export function statusColorClass(status: SharedSessionStatus): string {
  return status === 'failed'
    ? 'bg-red-500'
    : status === 'running'
      ? 'bg-emerald-500'
      : status === 'connecting'
        ? 'bg-amber-400'
        : 'bg-slate-400';
}

export function statusPingColorClass(status: SharedSessionStatus): string {
  return status === 'failed'
    ? 'bg-red-400'
    : status === 'running'
      ? 'bg-emerald-400'
      : status === 'connecting'
        ? 'bg-amber-300'
        : 'bg-slate-300';
}

export function countdownColorClass(session: SessionState): string {
  return session.status === 'WAITING'
    ? 'text-red-400'
    : session.countdown > 3
      ? 'text-emerald-400'
      : session.countdown > 0
        ? 'text-amber-400'
        : 'text-slate-500';
}

export function sessionStatusColorClass(status: string): string {
  return status === 'BETTING' ? 'text-emerald-400' :
    status === 'WAITING' ? 'text-amber-400' :
    status === 'ROLLING' ? 'text-purple-400' :
    status === 'ENDED' ? 'text-slate-400' :
    'text-slate-500';
}

// ─── Shared hook: shared session + group status ────────────────────

/**
 * Hook chung cho group livestream modal:
 *   - Tìm account đang RUNNING để subscribe shared session
 *   - useSharedSession (SSE session data đã parse sẵn)
 *   - Tính overallStatus từ sessionStatus + member statuses
 *
 * Trả về session, status, runningMember, anyRunning, overallStatus.
 */
export function useGroupLivestream(
  members: IAccountDataResponse[],
  isOpen: boolean,
  gameType: string | undefined,
) {
  const runningMember = members.find((m) => m.status === 'RUNNING') ?? members[0];
  const anyRunning = members.some((m) => m.status === 'RUNNING');

  // Nhóm đã Stop vẫn mở được modal (để xem lại lịch sử phiên) nhưng KHÔNG còn WS
  // nào đẩy session data — mở stream lúc đó chỉ để nó lỗi rồi retry mãi.
  const { session, status: sessionStatus } = useSharedSession(
    gameType,
    isOpen && anyRunning && !!gameType && gameType !== '?',
  );
  const overallStatus: SharedSessionStatus | 'idle' = anyRunning
    ? sessionStatus === 'failed'
      ? 'failed'
      : sessionStatus === 'running'
        ? 'running'
        : 'connecting'
    : 'idle';

  return {
    session,
    sessionStatus,
    runningMember,
    anyRunning,
    overallStatus,
  };
}

// ─── Shared UI: member dots header ─────────────────────────────────

export const GroupMemberDots: React.FC<{ members: IAccountDataResponse[] }> = ({ members }) => (
  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-400">
    {members.map((m) => {
      const isConnected = m.status === 'RUNNING';
      return (
        <span key={m._id || m.username} className="flex items-center gap-1">
          <span
            className={`inline-flex h-2 w-2 rounded-full ${
              isConnected ? 'bg-emerald-500' : 'bg-red-500'
            }`}
          />
          <span className="text-[10px] font-bold text-slate-300">{m.username}</span>
        </span>
      );
    })}
    {members.length === 0 && (
      <span className="text-slate-500">Nhóm chưa có thành viên.</span>
    )}
  </div>
);

// ─── Re-export format helpers ──────────────────────────────────────

export { formatJackpot, formatCoin };